import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { internal_backend } from '~/internal-backend'
import { supabaseApi } from '~/supabase/api'
import { ModelListRowModel } from '../types'
import { GET_SHOPIFY_PRODUCTS_QUERY_KEY } from '~/features/shopify/hooks/useGetShopifyProducts'
import { GET_INVENTORY_QUERY_KEY } from '~/features/hooks/useGetInventory'

export const useDeleteFromShopify = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['delete-from-shopify'],
    mutationFn: async (rows: ModelListRowModel[]) => {
      // Step 1: Delete Shopify products in parallel
      await Promise.all(
        rows.map((row) =>
          internal_backend.delete_product.request({ productId: row.shopify_product_id! })
        )
      )

      // Step 2: Patch Supabase models in parallel
      await Promise.all(
        rows.map((row) =>
          supabaseApi
            .from('models_v2')
            .update({ status_v2: 'ready', shopify_product_id: null })
            .eq('id', row.id)
        )
      )

      // Step 3: Clear stale Shopify inventory IDs for all inventory rows of those models
      await Promise.all(
        rows.map((row) =>
          supabaseApi
            .from('inventory_v2')
            .update({ product_variant_id: null, inventory_item_id: null })
            .eq('stock_code_v2', row.stock_code)
        )
      )
    },
    onSuccess: () => {
      toast.success('Product removed from Shopify')
      queryClient.invalidateQueries({ queryKey: [GET_SHOPIFY_PRODUCTS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [GET_INVENTORY_QUERY_KEY] })
    },
    onError: () => {
      toast.error('Failed to remove product from Shopify')
    },
  })
}
